import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useToaster } from "contexts/Toaster";

/**
 * Translation key the server sends when a b2c account has reached its cap of
 * three own scenes in a project. The fetch interceptor translates any message
 * containing a dot, so this arrives as `translatedMessage` too — the key is
 * matched here rather than the text, which changes per language.
 */
export const SCENE_LIMIT_MESSAGE = "msg.scene.limitReached";

/**
 * Shows the "three scenes per project" toast when a scene-creating request was
 * rejected for that reason, and reports whether it was.
 *
 * Every route that creates a scene can return this — new scene, from-floorplan,
 * from-roomscan, copy and save-as — so the check lives here once instead of
 * being restated at each call site. It cannot live in the fetch interceptor:
 * FetchProvider wraps ToasterProvider, so it has no toaster to reach for.
 */
const useSceneLimitToast = () => {
	const { t } = useTranslation();
	const toaster = useToaster();

	return useCallback(
		(response?: { status?: number }, data?: any): boolean => {
			const isLimit =
				response?.status === 403 && data?.message === SCENE_LIMIT_MESSAGE;

			if (isLimit) {
				toaster.error({
					message: String(t("app.global.msg.title.note")),
					description: String(t(SCENE_LIMIT_MESSAGE)),
				});
			}

			return isLimit;
		},
		[t, toaster],
	);
};

export default useSceneLimitToast;
